import MaterialTable from '@material-table/core';
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';
import { Box, Card, CardContent } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { UserContext } from '../../contexts/UserContext';
import { createAuditLog, deleteCallbackConfiguration } from '../../graphql/mutations';
import { tenantCallbackConfigurations } from '../../graphql/queries';
import { cardStyle } from '../../theme';
import { fetchAllPages } from '../../tools/graphqlPaging';

export function CallbackConfigurations() {
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [configurations, setConfigurations] = useState([]);
    const [toDelete, setToDelete] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        async function getData() {
            setLoading(true);
            await getConfigurations();
        }
        if (userContext.tenantId) {
            getData();
        }
    }, [userContext.tenantId]);

    useEffect(() => {
        setLoading(false);
    }, [configurations]);

    async function getConfigurations() {
        // Paged so all callback configurations are listed, not just page one.
        const allConfigurations = await fetchAllPages({
            client,
            query: tenantCallbackConfigurations,
            resultKey: 'tenantCallbackConfigurations',
            variables: { tenant: userContext.tenantId },
        });
        setConfigurations(
            allConfigurations.sort(
                (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
            ),
        );
    }

    async function handleDelete() {
        try {
            setLoading(true);

            await client.graphql({
                query: deleteCallbackConfiguration,
                variables: { input: { id: toDelete } },
            });
            await client.graphql({
                query: createAuditLog,
                variables: {
                    input: {
                        tenant: userContext.tenantId,
                        type: 'CallbackConfiguration',
                        timestamp: new Date().toISOString(),
                        userName: userContext.email,
                        documentId: toDelete,
                        name: configurations.find(config => config.id === toDelete).name,
                        currentValue: "{}",
                        action: 'DELETED',
                        ttl: Math.floor(Date.now() / 1000) + (6 * 30 * 24 * 60 * 60),
                    }
                }
            });
            setToDelete(null);
            setConfirmDeleteOpen(false);
            enqueueSnackbar('Configuration deleted successfully.', {
                variant: 'success',
                autoHideDuration: 2000
            });
            await getConfigurations();
            setLoading(false);

        } catch (e) {
            console.error(e);
            enqueueSnackbar('Error deleting configuration.', {
                variant: 'error',
                autoHideDuration: 2000
            });
            setLoading(false);
            setToDelete(null);
            setConfirmDeleteOpen(false);
        }
    }

    function handleCancelDelete() {
        setToDelete(null);
        setConfirmDeleteOpen(false);
    }

    return (
        <Box>
            <PageAppBar
                title="Callback Configurations"
                actionOneText={userContext.securityOptions.campaigns > 1 && "+ Configuration"}
                actionOneHandler={() => {
                    navigate('/callback-configurations/new');
                }}
                actionOneDisabled={userContext.securityOptions.campaigns < 2}
            />
            <Card variant='outlined' sx={cardStyle}>
                <CardContent>
                    <MaterialTable
                        components={{
                            Container: (props) => <Box {...props} elevation={0} />,
                        }}
                        title=""
                        data={configurations}
                        columns={[
                            { title: 'Name', field: 'name' },
                            { title: 'Max Per Day', field: 'maxPerDay' },
                            { title: 'Max Total', field: 'maxTotal' },
                            { title: 'Max Days Out', field: 'maxDaysOut' },
                            { title: 'Expire After Days', field: 'expireAfterDays' },
                        ]}
                        isLoading={loading}
                        options={{
                            actionsColumnIndex: -1,
                            pageSize: 10,
                            searchFieldStyle: {
                                marginBottom: '16px',
                                marginLeft: '-28px',
                            },
                            headerStyle: {
                                borderTop: '1px solid #e0e0e0',
                                padding: '16px',
                            },
                            searchFieldVariant: 'outlined',
                            paginationType: 'stepped',
                        }}
                        actions={[
                            {
                                disabled: userContext.securityOptions.campaigns < 2,
                                icon: () => <EditOutlined color="primary" />,
                                tooltip: 'Edit Configuration',
                                onClick: (event, rowData) => {
                                    navigate(`/callback-configurations/${rowData.id}`);
                                },
                            },
                            rowData => function () {
                                const disabled = userContext.securityOptions.campaigns < 2;
                                return {
                                    icon: () => <DeleteOutlineOutlined color={disabled ? 'disabled' : 'primary'} />,
                                    tooltip: 'Delete Configuration',
                                    onClick: (event, rowData) => {
                                        setToDelete(rowData.id);
                                        setConfirmDeleteOpen(true);
                                    },
                                    disabled
                                }
                            }(),
                        ]}
                        onRowClick={(event, rowData) => {
                            navigate(`/callback-configurations/${rowData.id}`);
                        }}
                    />
                </CardContent>
            </Card>
            <ConfirmDialog
                open={confirmDeleteOpen}
                title="Delete Configuration"
                description="Are you sure you want to delete this callback configuration?"
                actionOneText="Delete"
                actionOneHandler={() => {
                    handleDelete();
                }}
                actionTwoText="Cancel"
                actionTwoHandler={() => {
                    handleCancelDelete();
                }}
            />
        </Box>
    );
}
